import React from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './logo.png';
import { 
  Check, 
  Sparkles, 
  Award, 
  ShieldCheck, 
  ArrowRight, 
  Star, 
  Users, 
  Heart,
  Truck,
  Factory,
  Store
} from 'lucide-react';

const About = () => {
  const navigate = useNavigate();

  const stats = [
    { number: "1500+", label: "Happy Customers" },
    { number: "3000+", label: "Jewellery Pieces Sold" },
    { number: "500+", label: "Custom Creations" },
    { number: "40+", label: "Years of Excellence" }
  ];

  const pillars = [
    {
      icon: <Award className="w-6 h-6 text-[#eddca6]" />,
      title: "Heritage Crafts",
      description: "Collections inspired by Wayanad's rich cultural heritage, blending time-honored traditional techniques with modern aesthetics."
    },
    {
      icon: <ShieldCheck className="w-6 h-6 text-[#eddca6]" />,
      title: "BIS 916 Hallmarked",
      description: "Every single piece of gold is rigorously tested and 100% BIS hallmarked. All diamonds are internationally GIA certified."
    },
    {
      icon: <Users className="w-6 h-6 text-[#eddca6]" />,
      title: "Custom Creations",
      description: "Collaborate directly with our master goldsmiths to craft custom bridal sets, unique rings, and heirloom pieces."
    },
    {
      icon: <Heart className="w-6 h-6 text-[#eddca6]" />,
      title: "Generational Trust",
      description: "Built on four decades of honesty, transparent gold weight calculations, and dedicated customer relationships."
    }
  ];

  return (
    <div className="bg-[#fafaf7] text-[#0b3a24] font-sans overflow-hidden min-h-screen">
      
      {/* Hero Header Banner (Dark Kerala Emerald Green & Gold) */}
      <section className="relative bg-gradient-to-br from-[#041a0f] via-[#09100d] to-[#020906] text-white pt-36 pb-24 px-4 sm:px-6 lg:px-8 border-b border-[#eddca6]/20 overflow-hidden">
        
        {/* Soft Ambient Light Glow Blobs */}
        <div className="absolute top-1/3 left-1/4 -translate-x-1/2 w-[500px] h-[500px] bg-gradient-to-tr from-gold-900/20 to-transparent rounded-full filter blur-[140px] pointer-events-none"></div>
        <div className="absolute bottom-10 right-10 w-[300px] h-[300px] bg-[#eddca6]/10 rounded-full filter blur-[100px] pointer-events-none"></div>

        {/* Decorative Floating Sparkles */}
        <div className="absolute top-16 right-[12%] pointer-events-none animate-pulse">
          <Sparkles className="w-10 h-10 text-[#c5a85a]/40" />
        </div>
        <div className="absolute bottom-12 left-[8%] pointer-events-none">
          <Star className="w-8 h-8 text-[#c5a85a]/25 animate-spin-slow" />
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10 space-y-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex items-center gap-2 bg-[#eddca6]/10 border border-[#eddca6]/30 text-[#eddca6] px-4.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-sm">
              <Award className="w-3.5 h-3.5 text-[#eddca6]" />
              ESTABLISHED SINCE 1985
            </div>
            <div className="inline-flex items-center gap-2 bg-[#eddca6]/15 border border-[#eddca6]/40 text-[#eddca6] px-4.5 py-1.5 rounded-full text-[10px] sm:text-xs font-bold uppercase tracking-wider shadow-sm">
              <Sparkles className="w-3.5 h-3.5 text-[#eddca6]" />
              <span>A unit from Vaikom Manufacturer • Wholesale and Retail</span>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-black tracking-widest uppercase leading-tight text-white drop-shadow-md">
            ABOUT <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#eddca6] via-[#f5edd2] to-[#c5a85a]">CHIRAYIL</span>
          </h1>

          <p className="max-w-2xl mx-auto text-xs sm:text-sm text-white/80 font-light leading-relaxed">
            A unit from Vaikom Manufacturer — Wholesale and Retail. Located in the heart of Pulpally, Chirayil Gold and Diamonds has spent over four decades crafting timeless Kerala treasures that blend tradition, purity, and modern elegance.
          </p>

          <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#eddca6] to-transparent mx-auto pt-2"></div>
        </div>
      </section>

      {/* Narrative & Craftsmanship Section */}
      <section className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-white border-b border-gold-200">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            
            {/* Left Side: Overlapping Image Collage */}
            <div className="relative flex justify-center items-center py-6 lg:py-0">
              {/* Background Decorative Rings */}
              <div className="absolute w-[340px] sm:w-[420px] h-[340px] sm:h-[420px] border border-gold-200/50 rounded-full pointer-events-none"></div>
              <div className="absolute w-[260px] sm:w-[320px] h-[260px] sm:h-[320px] border border-dashed border-gold-300/40 rounded-full pointer-events-none"></div>

              {/* Main Goldsmith Craftsmanship Photo */}
              <div className="relative w-[260px] sm:w-[320px] aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl border-2 border-[#eddca6] z-10 transform -rotate-2 hover:rotate-0 transition-transform duration-700">
                <img 
                  src="https://images.unsplash.com/photo-1573408301185-9146fe634ad0?q=80&w=800" 
                  alt="Indian goldsmith crafting traditional gold jewelry" 
                  className="w-full h-full object-cover filter contrast-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#041a0f]/40 to-transparent"></div>
              </div>

              {/* Overlapping Brand Seal Card */}
              <div className="absolute -right-2 sm:right-4 bottom-2 w-[160px] sm:w-[190px] aspect-square bg-[#fafaf7] rounded-3xl p-5 border-2 border-[#eddca6] shadow-2xl z-20 transform rotate-6 hover:rotate-0 transition-all duration-700 flex flex-col items-center justify-center text-center group">
                <img 
                  src={logo} 
                  alt="Chirayil Logo Emblem" 
                  className="w-14 h-14 object-contain mb-2 transition-transform group-hover:scale-105" 
                />
                <span className="text-[10px] font-bold text-[#0b3a24] uppercase tracking-widest font-sans">Pulpally</span>
                <span className="text-[8px] text-gold-600 uppercase tracking-widest mt-0.5 font-sans">Wayanad, Kerala</span>
              </div>
            </div>

            {/* Right Side: Detailed Narrative */}
            <div className="space-y-8 z-10">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 border border-gold-300 px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#0b3a24] bg-[#fafaf7] shadow-sm font-sans">
                  <Sparkles className="w-3.5 h-3.5 text-[#0b3a24]" />
                  OUR HERITAGE STORY
                </div>

                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-black text-[#0b3a24] leading-tight">
                  Crafting Moments into <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0b3a24] via-[#c5a85a] to-[#0b3a24] italic font-serif">
                    Timeless Heirlooms
                  </span>
                </h2>
                <div className="w-16 h-0.5 bg-[#0b3a24]"></div>
              </div>

              <p className="text-[#0b3a24]/85 text-base leading-relaxed font-light">
                At Chirayil Gold and Diamonds, we believe jewellery is far more than adornment—it's an emotional expression of your family’s milestones, legacy, and treasured dreams.
              </p>

              {/* Key Highlights */}
              <div className="space-y-4 pt-2">
                
                <div className="flex gap-4 p-4.5 rounded-2xl bg-[#fafaf7] border border-gold-250 hover:border-[#0b3a24]/30 hover:shadow-md transition-all duration-300 group">
                  <div className="w-9 h-9 bg-white border border-gold-300 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0b3a24] transition-colors duration-300">
                    <Check className="w-4 h-4 text-[#0d5c3a] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#0b3a24] uppercase tracking-wider mb-1 font-sans">Heritage & Contemporary Collections</h4>
                    <p className="text-[#0b3a24]/70 text-xs leading-relaxed font-light">
                      Curated designs inspired by traditional Kerala bridal ornaments and modern minimalist trends.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4.5 rounded-2xl bg-[#fafaf7] border border-gold-250 hover:border-[#0b3a24]/30 hover:shadow-md transition-all duration-300 group">
                  <div className="w-9 h-9 bg-white border border-gold-300 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0b3a24] transition-colors duration-300">
                    <Check className="w-4 h-4 text-[#0d5c3a] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#0b3a24] uppercase tracking-wider mb-1 font-sans">Uncompromising Purity & Transparency</h4>
                    <p className="text-[#0b3a24]/70 text-xs leading-relaxed font-light">
                      Guaranteed 916 BIS hallmarked gold and certified GIA diamonds with complete pricing clarity.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4 p-4.5 rounded-2xl bg-[#fafaf7] border border-gold-250 hover:border-[#0b3a24]/30 hover:shadow-md transition-all duration-300 group">
                  <div className="w-9 h-9 bg-white border border-gold-300 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#0b3a24] transition-colors duration-300">
                    <Check className="w-4 h-4 text-[#0d5c3a] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-[#0b3a24] uppercase tracking-wider mb-1 font-sans">Personalized Customer Service</h4>
                    <p className="text-[#0b3a24]/70 text-xs leading-relaxed font-light">
                      A welcoming showroom experience with dedicated private bridal consultations and custom design assistance.
                    </p>
                  </div>
                </div>

              </div>

              {/* Signature Quote Callout */}
              <div className="p-5 border-l-2 border-[#eddca6] bg-[#fafaf7] rounded-r-2xl italic text-[#0b3a24]/90 text-sm font-serif">
                “Every gram of gold from Chirayil carries the warmth of trust, crafted to shine across generations.”
              </div>

              <div className="pt-2">
                <button 
                  onClick={() => navigate('/')}
                  className="gold-shine-btn bg-gradient-to-r from-[#0b3a24] via-[#0e482d] to-[#0b3a24] text-white font-bold px-9 py-4 rounded-xl text-xs uppercase tracking-widest transition-all duration-300 shadow-lg hover:scale-105 active:scale-95 flex items-center gap-3 font-sans"
                >
                  <span>Explore Home & Schemes</span>
                  <ArrowRight className="w-4 h-4 text-[#eddca6]" />
                </button>
              </div>

            </div>

          </div>
        </div>
      </section>

      {/* SPECIAL SECTION: Wholesale, Making of Jewellery & Direct Retail */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 bg-white border-b border-gold-300 relative z-10">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center max-w-3xl mx-auto space-y-6 mb-20">
            <div className="inline-flex items-center gap-2 bg-[#0b3a24] text-[#eddca6] px-4.5 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest shadow-sm">
              <Award className="w-3.5 h-3.5 text-[#eddca6]" />
              <span>A UNIT FROM VAIKOM MANUFACTURER</span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-black text-[#0b3a24] leading-tight">
              Mainly Wholesale & Making — <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0b3a24] via-[#c5a85a] to-[#0b3a24] italic font-serif">
                Open for Retail Families
              </span>
            </h2>

            <div className="w-20 h-0.5 bg-[#0b3a24] mx-auto"></div>

            <p className="text-sm sm:text-base text-[#0b3a24]/85 leading-relaxed font-light">
              Our roots as a primary manufacturing house from <strong className="font-semibold text-[#0b3a24]">Vaikom</strong> give us a distinct advantage in Kerala's jewellery landscape. Because our primary business is <strong className="font-semibold text-[#0b3a24]">wholesale supply and in-house jewellery making</strong>, we eliminate middlemen and pass wholesale pricing and uncompromising BIS 916 purity directly to our <strong className="font-semibold text-[#0b3a24]">retail showroom visitors</strong>.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Wholesale Card */}
            <div className="bg-[#fafaf7] p-8 rounded-3xl border border-gold-300/70 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/40 transition-all duration-500 flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#0b3a24] to-[#0e4e30] rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Truck className="w-6 h-6 text-[#eddca6]" />
                </div>
                <div className="text-[10px] font-bold text-[#c5a85a] uppercase tracking-widest">Main Focus • Bulk Supply</div>
                <h3 className="text-2xl font-serif font-bold text-[#0b3a24]">Wholesale Supply</h3>
                <p className="text-xs text-[#0b3a24]/75 leading-relaxed font-light">
                  We are trusted bulk suppliers of BIS 916 hallmarked gold and certified diamond ornaments to showrooms and merchants across Kerala with competitive wholesale pricing.
                </p>
              </div>
              <div className="w-8 h-0.5 bg-[#0b3a24]/20 group-hover:bg-[#0b3a24] transition-colors mt-6"></div>
            </div>

            {/* Custom Making Card */}
            <div className="bg-gradient-to-br from-[#0b3a24] via-[#0e482d] to-[#072517] text-white p-8 rounded-3xl border-2 border-[#eddca6] shadow-xl hover:shadow-2xl transition-all duration-500 flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#eddca6] to-[#c5a85a] rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Factory className="w-6 h-6 text-[#0b3a24]" />
                </div>
                <div className="text-[10px] font-bold text-[#eddca6] uppercase tracking-widest">Vaikom Roots • In-House Making</div>
                <h3 className="text-2xl font-serif font-bold text-white">Jewellery Making</h3>
                <p className="text-xs text-white/90 leading-relaxed font-light">
                  Originating from Vaikom—historically celebrated for master goldsmiths—we cast, hand-engrave, and finish traditional Kerala bridal sets and custom pieces in-house.
                </p>
              </div>
              <div className="w-8 h-0.5 bg-[#eddca6]/40 group-hover:bg-[#eddca6] transition-colors mt-6"></div>
            </div>

            {/* Direct Retail Card */}
            <div className="bg-[#fafaf7] p-8 rounded-3xl border border-gold-300/70 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/40 transition-all duration-500 flex flex-col justify-between group">
              <div className="space-y-4">
                <div className="w-12 h-12 bg-gradient-to-br from-[#0b3a24] to-[#0e4e30] rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                  <Store className="w-6 h-6 text-[#eddca6]" />
                </div>
                <div className="text-[10px] font-bold text-[#c5a85a] uppercase tracking-widest">Pulpally Showroom • Direct Access</div>
                <h3 className="text-2xl font-serif font-bold text-[#0b3a24]">Direct Retail</h3>
                <p className="text-xs text-[#0b3a24]/75 leading-relaxed font-light">
                  Families buy directly from our showroom at manufacturer-direct wholesale pricing, enjoying zero middleman markup and complete purity clarity on every purchase.
                </p>
              </div>
              <div className="w-8 h-0.5 bg-[#0b3a24]/20 group-hover:bg-[#0b3a24] transition-colors mt-6"></div>
            </div>
          </div>

        </div>
      </section>

      {/* Core Pillars Grid Section */}
      <section className="py-28 px-4 sm:px-6 lg:px-8 bg-[#fafaf7] border-b border-gold-200 relative">
        <div className="max-w-7xl mx-auto">
          
          <div className="text-center mb-16 space-y-4">
            <p className="text-xs font-bold text-[#0b3a24] tracking-[0.25em] uppercase font-sans">OUR GUIDING PRINCIPLES</p>
            <h2 className="text-4xl sm:text-5xl font-serif font-black text-[#0b3a24]">
              The Pillars of <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#0b3a24] via-[#c5a85a] to-[#0b3a24] font-black">Chirayil</span>
            </h2>
            <div className="w-24 h-0.5 bg-gradient-to-r from-transparent via-[#0b3a24]/30 to-transparent mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-3xl border border-gold-300/70 shadow-sm hover:shadow-xl hover:border-[#0b3a24]/30 transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between group"
              >
                <div className="space-y-5">
                  <div className="w-12 h-12 bg-[#0b3a24] rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                    {pillar.icon}
                  </div>
                  <h3 className="text-xl font-serif font-bold text-[#0b3a24] group-hover:text-[#c5a85a] transition-colors">
                    {pillar.title}
                  </h3>
                  <p className="text-xs text-[#0b3a24]/75 font-light leading-relaxed">
                    {pillar.description}
                  </p>
                </div>
                <div className="w-8 h-0.5 bg-[#0b3a24]/20 group-hover:bg-[#0b3a24] transition-colors mt-8"></div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Stats Counter Section */}
      <section className="bg-gradient-to-r from-[#072a19] via-[#041a0f] to-[#020906] py-20 px-4 text-white border-b border-[#eddca6]/20">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-white/10">
            {stats.map((stat, index) => (
              <div key={index} className="pt-6 md:pt-0 space-y-2">
                <div className="text-4xl sm:text-5xl font-serif font-black text-[#eddca6]">
                  {stat.number}
                </div>
                <div className="text-[10px] font-bold tracking-[0.25em] text-white/70 uppercase font-sans">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Banner */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto bg-gradient-to-br from-[#0b3a24] via-[#072a19] to-[#041a0f] rounded-3xl p-10 sm:p-14 text-white shadow-2xl border border-[#eddca6]/30 text-center relative overflow-hidden">
          
          <div className="absolute top-0 right-0 w-48 h-48 bg-[#eddca6]/10 rounded-full blur-3xl pointer-events-none"></div>

          <div className="relative z-10 space-y-6 max-w-2xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[#eddca6]">
              Begin Your Gold Journey With Us
            </h2>
            <p className="text-xs sm:text-sm text-white/80 font-light leading-relaxed">
              Visit our flagship showroom in Pulpally or join our monthly Gold Savings Schemes online today.
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => navigate('/schemes')}
                className="gold-shine-btn bg-gradient-to-r from-[#eddca6] via-[#fff3d1] to-[#eddca6] text-[#041a0f] font-sans font-extrabold text-xs uppercase tracking-widest px-8 py-4 rounded-xl shadow-xl hover:scale-105 transition-all"
              >
                Join Savings Scheme
              </button>
              <button 
                onClick={() => navigate('/contact')}
                className="border border-[#eddca6]/40 hover:bg-white/10 text-white font-bold text-xs uppercase tracking-widest px-8 py-4 rounded-xl transition-all font-sans"
              >
                Contact Showroom
              </button>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default About;